import { useSelector } from 'react-redux'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { useMemo } from 'react'
import { useGetServiceLocationUrl } from '@user/store/authorization/hooks'
import { useGetSalesTeamUsers } from '@user/store/salesTeam/hooks'
import { useGetSalesFunnelData } from '@user/components/Settings/SalesFunnels/hooks'
import { ENTITY_TYPES } from '@sales-center/common'
import { selectAllDivisions } from '@user/store/divisions/slice'
import { selectAllLocationTypes } from '@user/store/locationTypes/slice'
import { selectAllCampaigns } from '@user/store/campaigns/slice'
import { selectAllSources } from '@user/store/sources/slice'
import { useGetEntityFormDataOnMount } from '@user/components/Settings/EntityFormSettings/hooks'
import { getNameRequirementMode } from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { useCreateOppActions } from '@user/components/Location'
import { useGetLocationOnMount } from '@user/store/locations'
import {
  useCustomFieldsVisible,
  useSelectCustomFieldData,
} from '@user/store/customFields'
import { useLocation } from 'react-router-dom'
import {
  addLeadAttributionSection,
  addLeadLeadSection,
  addLeadLocationSection,
  addLeadMarketingAttributionSection,
  addLeadOtherSection,
  entityNextActionDate,
} from '@user/components/Settings/EntityFormSettings'
import { ILeadFormModel } from '../../typings'
import { useFormikContext } from 'formik'
import { useGetAddLeadDataOnMount } from '../../hooks'
import {
  formatInitialPhoneNumberValue,
  getInitialValues,
  getInitialValuesForLocation,
} from '../../initialValues'
import { selectAllTargetPests } from '@user/store/targetPests'
import {
  selectCurrentUser,
  useCheckPermissions,
} from '@user/store/authorization'

type Props = {
  hideOptional: boolean
}

export function useData(props: Props) {
  const { hideOptional } = props
  const { values } = useFormikContext<ILeadFormModel>()

  const {
    branch,
    branchId,
    externalLocationCode,
    externalLocationId,
    salesFunnelId,
    salesTeamId,
  } = values

  const allSalesTeams = useSelector(selectAllSalesTeams)

  const locationUrl = useGetServiceLocationUrl(externalLocationId)

  const leadLocationInfo = {
    branch: branch,
    externalLocationId: externalLocationId,
    externalLocationCode: externalLocationCode,
    externalLocationUrl: locationUrl,
  }

  const unsortedSalesTeams = useMemo(() => {
    return branchId
      ? allSalesTeams.filter((t) => t.branchId === branchId)
      : allSalesTeams
  }, [allSalesTeams, branchId])

  const salesTeams = useMemo(() => {
    if (unsortedSalesTeams.find((x) => x.branchDefault === true)) {
      return unsortedSalesTeams.sort((a, b) => {
        if (a.branchDefault) return -1
        else if (b.branchDefault) return 1
        return a.name.localeCompare(b.name)
      })
    } else if (unsortedSalesTeams.find((x) => x.tenantDefault === true)) {
      return unsortedSalesTeams.sort((a, b) => {
        if (a.tenantDefault) return -1
        else if (b.tenantDefault) return 1
        return a.name.localeCompare(b.name)
      })
    } else {
      return unsortedSalesTeams
    }
  }, [unsortedSalesTeams])

  const defaultSalesTeam = useMemo(() => {
    if (
      unsortedSalesTeams
        .filter((x) => branchId == x.branchId)
        .find((x) => x.branchDefault === true)
    ) {
      return unsortedSalesTeams
        .filter((x) => branchId == x.branchId && x.branchDefault)
        .pop()
    } else if (unsortedSalesTeams.find((x) => x.tenantDefault === true)) {
      return unsortedSalesTeams.filter((x) => x.tenantDefault).pop()
    }
  }, [branchId, unsortedSalesTeams])

  const { execute: loadSalesTeamUsers } = useGetSalesTeamUsers(
    salesTeamId == '' ? undefined : +salesTeamId,
  )

  // //#region sales funnels
  const { defaultFunnel, firstStage, salesFunnels, salesFunnelStages } =
    useGetSalesFunnelData(ENTITY_TYPES.Lead, +salesFunnelId)
  //#endregion

  const divisions = useSelector(selectAllDivisions)
  const locationTypes = useSelector(selectAllLocationTypes)
  const leadSources = useSelector(selectAllSources)
  const campaigns = useSelector(selectAllCampaigns)

  const {
    entitySettingsMap,
    settingsBySection,
    requiredEntitySettings,
    customFieldSettings,
    settings,
  } = useGetEntityFormDataOnMount(ENTITY_TYPES.Lead, hideOptional)

  const nameRequirementMode = useMemo(
    () => getNameRequirementMode(settings, ENTITY_TYPES.Lead),
    [settings],
  )

  const showFollowUpSection = useMemo(() => {
    return (
      (!hideOptional &&
        settings.find(
          (x) =>
            x.active &&
            (x.fieldId === 'preferredMethodOfContact' ||
              x.fieldId === 'preferredTimeOfContact'),
        )) ||
      settings.find(
        (x) =>
          x.required &&
          (x.fieldId === 'preferredMethodOfContact' ||
            x.fieldId === 'preferredTimeOfContact'),
      )
    )
  }, [hideOptional, settings])

  const activeLeadSection = useMemo(() => {
    return (
      (!hideOptional &&
        settingsBySection[addLeadLeadSection].find((x) => x.active)) ||
      settingsBySection[addLeadLeadSection].find((x) => x.required)
    )
  }, [hideOptional, settingsBySection])

  const activeLocationSection = useMemo(() => {
    return (
      (!hideOptional &&
        settingsBySection[addLeadLocationSection].find((x) => x.active)) ||
      settingsBySection[addLeadLocationSection].find((x) => x.required)
    )
  }, [hideOptional, settingsBySection])

  const activeMarketingAttributionSection = useMemo(() => {
    return (
      (!hideOptional &&
        settingsBySection[addLeadMarketingAttributionSection].find(
          (x) => x.active,
        )) ||
      settingsBySection[addLeadMarketingAttributionSection].find(
        (x) => x.required,
      ) ||
      (!hideOptional &&
        settingsBySection[addLeadAttributionSection].find((x) => x.active)) ||
      settingsBySection[addLeadAttributionSection].find((x) => x.required)
    )
  }, [hideOptional, settingsBySection])

  const { hasEnabledCustomFields, hasRequiredCustomFields } =
    useCustomFieldsVisible()

  const activeOtherSection = useMemo(() => {
    return (
      (!hideOptional &&
        (settingsBySection[addLeadOtherSection].find((x) => x.active) ||
          hasEnabledCustomFields)) ||
      settingsBySection[addLeadOtherSection].find((x) => x.required) ||
      hasRequiredCustomFields
    )
  }, [
    hideOptional,
    settingsBySection,
    hasEnabledCustomFields,
    hasRequiredCustomFields,
  ])

  const showOtherSection =
    activeOtherSection && entitySettingsMap.has(entityNextActionDate)

  const createOppActions = useCreateOppActions(true)

  const location = useLocation()

  const { draftIdParam, locationIdParam, phoneParam } = useMemo(() => {
    const params = new URLSearchParams(location.search)

    return {
      phoneParam: params.get('phone'),
      draftIdParam: params.get('draftId'),
      locationIdParam: params.get('locationId'),
    }
  }, [location.search])

  const { loading: dataLoading, draft: draftLead } =
    useGetAddLeadDataOnMount(draftIdParam)

  const locationId = draftLead?.locationId ?? locationIdParam

  const isDraftLead = !!draftLead

  const isAddFromLocation = locationId && !isDraftLead

  const { serviceLocation, loadingStatus } = useGetLocationOnMount(locationId)

  const loading = dataLoading || loadingStatus

  const { customFields } = useSelectCustomFieldData()

  const allTargetPests = useSelector(selectAllTargetPests)

  const initialValues = useMemo<ILeadFormModel>(() => {
    const phoneFormated = formatInitialPhoneNumberValue(phoneParam)
    if (locationId && !draftLead) {
      return getInitialValuesForLocation(
        customFields,
        serviceLocation,
        phoneFormated,
      )
    } else {
      return getInitialValues(
        customFields,
        draftLead,
        allTargetPests,
        serviceLocation,
        phoneFormated,
      )
    }
  }, [
    locationId,
    draftLead,
    customFields,
    serviceLocation,
    phoneParam,
    allTargetPests,
  ])

  const [canUpdateLocations] = useCheckPermissions('accounts_update')
  const { externalUserId } = useSelector(selectCurrentUser)
  const disabled = locationId ? true : false

  const isMappedWithUpdateLocationPerm = Boolean(
    canUpdateLocations && externalUserId,
  )

  return {
    activeAttributionSection: activeMarketingAttributionSection,
    showFollowUpSection,
    activeLeadSection,
    activeOtherSection,
    activeLocationSection,
    allSalesTeams,
    campaigns,
    defaultFunnel,
    defaultSalesTeam,
    divisions,
    entitySettingsMap,
    firstStage,
    hasEnabledCustomFields,
    hasRequiredCustomFields,
    leadLocationInfo,
    leadSources,
    loadSalesTeamUsers,
    locationTypes,
    locationUrl,
    nameRequirementMode,
    salesFunnels,
    salesFunnelStages,
    salesTeams,
    settingsBySection,
    showOtherSection,
    unsortedSalesTeams,
    requiredEntitySettings,
    customFieldSettings,
    disabled,
    isMappedWithUpdateLocationPerm,
    initialValues,
    loading,
    isAddFromLocation,
    createOppActions,
  }
}

export type UseData = ReturnType<typeof useData>
